import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Flame, Zap, Star, Crown, Rocket, Target, Trophy, Shield, FlaskConical, Loader2 } from "lucide-react";

export const AVATAR_ICONS = { flame: Flame, zap: Zap, star: Star, crown: Crown, rocket: Rocket, target: Target, trophy: Trophy, shield: Shield };

export const Avatar = ({ user, size = 36 }) => {
  const [broken, setBroken] = useState(false);
  const uid = user?.id || user?.user_id;
  const photo = typeof user?.avatar === "string" && user.avatar.startsWith("img:") && uid && !broken;
  const Icon = user?.avatar ? AVATAR_ICONS[user.avatar] : null;
  const initials = (user?.display_name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-[#1E2A4A] font-bold text-white"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {photo ? <img src={`${API}/avatars/${uid}?v=${user.avatar.slice(4)}`} alt={`Photo de ${user.display_name || "membre"}`} loading="lazy" onError={() => setBroken(true)} className="h-full w-full object-cover" />
        : Icon ? <Icon size={size * 0.5} className="text-[#B7F52A]" /> : initials}
    </span>
  );
};

const TEAM_COLORS = { ATL: "#E03A3E", BOS: "#007A33", BKN: "#000000", CHA: "#1D1160", CHI: "#CE1141", CLE: "#860038", DAL: "#00538C", DEN: "#0E2240",
  DET: "#C8102E", GSW: "#1D428A", HOU: "#CE1141", IND: "#002D62", LAC: "#C8102E", LAL: "#552583", MEM: "#5D76A9", MIA: "#98002E", MIL: "#00471B",
  MIN: "#0C2340", NOP: "#0C2340", NYK: "#006BB6", OKC: "#007AC1", ORL: "#0077C0", PHI: "#006BB6", PHX: "#1D1160", POR: "#E03A3E", SAC: "#5A2D81",
  SAS: "#4B5259", TOR: "#CE1141", UTA: "#002B5C", WAS: "#002B5C" };

export const TeamMark = ({ team, size = 44 }) => (
  <span
    className="mpl-team inline-flex shrink-0 items-center justify-center rounded-xl border border-white/10 bg-[#05070D] font-display font-extrabold text-white"
    style={{ width: size, height: size, fontSize: size * 0.32, "--team": TEAM_COLORS[team?.abbr] || "#2A312A" }}
    aria-hidden="true"
  >
    {team?.abbr}
  </span>
);

export const DemoBadge = ({ className = "" }) => (
  <span
    data-testid="demo-badge"
    className={`inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300 ${className}`}
  >
    <FlaskConical size={11} /> Démo · fictif
  </span>
);

export const PageHeader = ({ eyebrow, title, action, children }) => (
  <header className="mb-6 flex flex-wrap items-end justify-between gap-4 mpl-rise">
    <div className="min-w-0">
      {eyebrow && <p className="mpl-eyebrow mb-1.5">{eyebrow}</p>}
      <h1 className="break-words font-display text-3xl font-extrabold uppercase leading-none text-white sm:text-4xl">{title}</h1>
      {children}
    </div>
    {action}
  </header>
);

export const EmptyState = ({ icon: Icon, title, text, cta, to, testId = "empty-state" }) => (
  <div data-testid={testId} className="mpl-card flex flex-col items-start gap-3 p-6">
    {Icon && (
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#B7F52A]/10 text-[#B7F52A]">
        <Icon size={22} />
      </span>
    )}
    <h3 className="text-base font-bold text-white">{title}</h3>
    {text && <p className="text-sm text-slate-400">{text}</p>}
    {cta && to && (
      <Link to={to} className="mpl-btn-primary mt-1" data-testid={`${testId}-cta`}>
        {cta}
      </Link>
    )}
  </div>
);

export const Spinner = ({ label = "Chargement…" }) => (
  <div className="flex items-center gap-2 py-10 text-sm text-slate-400" data-testid="loading">
    <Loader2 size={18} className="animate-spin text-[#B7F52A]" /> {label}
  </div>
);

export const ErrorBox = ({ message, onRetry }) => (
  <div data-testid="error-box" className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
    <p>{message}</p>
    {onRetry && (
      <button onClick={onRetry} className="mt-2 font-semibold text-white underline" data-testid="error-retry-btn">
        Réessayer
      </button>
    )}
  </div>
);

export const Stat = ({ label, value, accent, testId }) => (
  <div className="mpl-card p-4">
    <p className="mpl-eyebrow">{label}</p>
    <p data-testid={testId} className={`mt-1 font-mono-num text-2xl font-bold ${accent ? "text-[#B7F52A]" : "text-white"}`}>
      {value}
    </p>
  </div>
);
